/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, defineMessages, FormattedMessage} from 'react-intl'
import loadable from '@loadable/component'

import {
    Flex,
    Heading,
    ButtonGroup,
    Button,
    IconButton,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useTheme,
    useBreakpointValue,
    useMultiStyleConfig,
    Tooltip,
    Icon,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HeartIcon, HeartSolidIcon, InfoIcon} from '../icons'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {useBazarVoice} from '../../integrations/bazarvoice/hooks/use-bazarvoice'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// project components
import ImageGallery from '../../components/image-gallery'
import Breadcrumb from '../../components/breadcrumb'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '../with-registration'
import {Skeleton as ImageGallerySkeleton} from '../../components/image-gallery'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useInView} from '../../hooks/use-in-view'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import DisplayPrice from '../display-price'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {getPriceData} from '../../utils/product-utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import StickyButton from './partials/StickyButton'
import Loyalty from './partials/Loyalty'
import StarRating from '../pdp-reviews/partials/star-rating'
import ActualFitMessage from '../actual-fit-message'
import {KlarnaScript} from '../../utils/klarna-script'
import ShopTheLook from '../../components/shop-the-look'
import {FewItemsLeftIcon, FewItemsLeftMessage} from '../few-items-left'
import GiftCardForm from './partials/giftCardForm'
import { RESOURCE_BADGE_MAPPING } from '../../constants'

// Lazy load modal/drawer components - only loaded when user interacts
// These are not needed for initial render and can be code-split
const NotifyMeModal = loadable(() => import('../notify-me-modal'), {ssr: false})
const ShopSimilarModal = loadable(() => import('../shop-similar-modal'), {ssr: false})
const SizeChartDrawer = loadable(() => import('../size-chart-drawer'), {ssr: false})

/* Hooks */
import {notifyMe} from '../../integrations/notifyme/hooks/use-notify-me'
import {useHeaderContext} from '../../contexts/header-context'
import {useStickyState} from '../../contexts/sticky-context'
import useGiftCard from '../../hooks/use-gift-card'

/* Icons */
import {NotifyMeEnvelope} from '../icons/index'
import InformationAccordion from '../../pages/product-detail/partials/information-accordion'
import {GiftCallout} from './partials/giftCallout'
import {GiftCardCheckBalance} from '../../pages/product-detail/partials/giftCardCheckBalance'

import {sendClickEvent} from '../../utils/gtm-utils'

const messages = defineMessages({
    notifyMeError: {
        defaultMessage: 'Failed to submit data',
        id: 'pdp_notify_me_general.error'
    }
})

const ProductViewHeader = ({
    name,
    currency,
    priceData,
    product,
    isProductPartOfBundle,
    onReviewsClick,
    isGiftPage = false,
    isLoading = false
}) => {
    const styles = useMultiStyleConfig('ProductTile')
    const intl = useIntl()
    const config = getConfig()
    const {isOpen, onOpen, onToggle, onClose} = useDisclosure()

    const promotionId = useMemo(
        () => product?.productPromotions?.[0]?.promotionId,
        [product?.productPromotions]
    )

    const calloutMsg = useMemo(
        () => product?.productPromotions?.[0]?.calloutMsg,
        [product?.productPromotions]
    )

    const promotionsQuery = usePromotions(
        {
            parameters: {ids: promotionId}
        },
        {
            enabled: !!promotionId
        }
    )

    const promotionData = promotionsQuery?.data?.data?.[0] || {}

    const renderProductCallout = () => {
        return <Heading
                as="h2"
                mt={{base: 4, lg: 8}}
                color="grey.700"
                {...styles.productTileLabel}
            >
            {product?.c_textBadges?.map((badge) => (
                <Box>
                    {intl.formatMessage(RESOURCE_BADGE_MAPPING[badge])}
                </Box>
            ))}
        </Heading>
    }

    const renderCalloutTooltip = () => {
        if (!promotionData?.c_showCalloutMessage || !calloutMsg) return null

        return (
            <Tooltip
                isOpen={isOpen}
                label={calloutMsg}
                {...styles.toolTip}
                mt="0"
                color="black"
                aria-label="calloutMsg"
                placement="right"
                hasArrow
                arrowShadowColor="gray.50"
                border="1px solid"
                borderColor="gray.50"
            >
                <IconButton
                    onMouseEnter={onOpen}
                    onMouseLeave={onClose}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onToggle()
                    }}
                    variant="unstyled"
                    h={'auto'}
                    minW={'auto'}
                    display={'flex'}
                    alignSelf={'center'}
                    icon={<InfoIcon {...styles.toolTipIcon} />}
                    aria-label="Tooltip"
                />
            </Tooltip>
        )
    }

    const renderPriceSection = () => {
        if (isProductPartOfBundle) return null

        return (
            <Skeleton isLoaded={!!priceData?.currentPrice && !isLoading}>
                <Flex alignItems="center">
                    {priceData?.currentPrice && (
                        <DisplayPrice
                            currentPriceProps={{
                                color: priceData?.isOnSale ? 'red.700' : 'black',
                                fontSize: 'md',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '6',
                                mr: 1
                            }}
                            listPriceProps={{
                                color: 'grey.500',
                                fontSize: 'md',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '6',
                                textDecoration: priceData?.isOnSale ? 'line-through' : 'none'
                            }}
                            priceData={priceData}
                            currency={currency}
                        />
                    )}
                    {renderCalloutTooltip()}
                </Flex>
            </Skeleton>
        )
    }

    const renderPromotionBadge = () => {
        if (!promotionData.c_showCalloutMessage || !promotionData.name) return null

        return (
            <Text
                fontWeight="medium"
                fontSize={{base: 'xs', lg: 'sm'}}
                color={priceData?.isOnSale ? 'black' : 'sale'}
            >
                {promotionData.name}
            </Text>
        )
    }

    return (
        <VStack mr={4} spacing={2} align="flex-start">
            {renderProductCallout()}

            <Skeleton isLoaded={!!name && !isLoading}>
                <Heading
                    as="h1"
                    variant="pdpH1"
                    mt={
                        product?.c_isFinalSale || product?.c_isNew || product?.c_isOnlineExclusive
                            ? 0
                            : {base: 4, lg: 8}
                    }
                    fontWeight={'medium'}
                    data-testid="product-name"
                >
                    {name}
                </Heading>
            </Skeleton>

            {!isGiftPage && renderPriceSection()}

            {renderPromotionBadge()}

            {config.app?.BV_ENABLE_RR_C2022 === 'true' && (
                <Reviews product={product} onReviewsClick={onReviewsClick} />
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    onReviewsClick: PropTypes.func,
    isGiftPage: PropTypes.bool,
    isLoading: PropTypes.bool
}

const Reviews = ({product, onReviewsClick}) => {
    const pid = product?.id
    const {isLoading, reviews: initialReviews, reviewsError} = useBazarVoice({pid})
    const [noReviews, setNoReviews] = useState(true)
    const averageRating =
        product?.c_bvAverageRating ||
        (initialReviews?.Results?.length > 0
            ? initialReviews.Results.reduce((sum, review) => sum + review.Rating, 0) /
              initialReviews.Results.length
            : null)

    const totalReviews = product?.c_bvReviewCount || initialReviews?.TotalResults || 0

    useEffect(() => {
        if (initialReviews?.Results) {
            setNoReviews(initialReviews.Results.length === 0)
        }
    }, [initialReviews])

    if (reviewsError) {
        console.error('[Reviews]:', reviewsError)
        return null
    }

    // If there are no reviews, don't show the component
    if ((!averageRating && totalReviews === 0) || noReviews) {
        return null
    }

    return (
        <Skeleton isLoaded={!isLoading}>
            <Flex
                as={'a'}
                href={'#pdp-reviews'}
                alignItems="center"
                gap="6px"
                cursor="pointer"
                role="button"
                tabIndex={0}
                aria-label="Star rating"
            >
                {averageRating && (
                    <StarRating
                        initialRating={Number(averageRating)}
                        lock={true}
                        styles={{starWidth: '12', starHeight: '12'}}
                    />
                )}
                {totalReviews > 0 && (
                    <Text fontSize="xs" color="black">
                        ({totalReviews})
                    </Text>
                )}
            </Flex>
        </Skeleton>
    )
}

Reviews.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        c_bvAverageRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        c_bvReviewCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    onReviewsClick: PropTypes.func
}

const ButtonWithRegistration = withRegistration(Button)
const IconButtonWithRegistration = withRegistration(IconButton)
const IconButtonWithRegistrationForWishlist = withRegistration(IconButton, {isWishlistAction: true})
const ButtonWithRegistrationForWishlist = withRegistration(Button, {isWishlistAction: true})

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product,
            category,
            showFullLink = false,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            onFindInStoreOpen,
            optimizelyContent,
            isOptimizelyLoading,
            isProductLoading,
            isMasterProductChanging = false,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            basket,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            isInWishlist = false,
            onReviewsClick
        },
        ref
    ) => {
        const config = getConfig()
        const notifyMeEnabled = config.app.NOTIFY_ME_ENABLED === 'true'

        const [isFewItemsLeft, setIsFewItemsLeft] = useState(false)
        const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
        const [notifyMeSuccessContent, setNotifyMeSuccessContent] = useState(false)
        const [notifyMeError, setNotifyMeError] = useState(null)
        const {isLoading: isNotifyMeLoading, submitNotifyMe} = notifyMe()
        const [showNotifyMeIcon, setShowNotifyMeIcon] = useState(false)
        const {data: customer} = useCurrentCustomer()
        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const navigate = useNavigation()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose
        } = useMiniCartContext()
        const theme = useTheme()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            stepQuantity,
            isOutOfStock,
            unfulfillable
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)
        const isNotifyMe = useMemo(() => {
            if (!notifyMeEnabled) {
                return false
            }
            if (!variant || typeof variant.orderable !== 'boolean') {
                return false
            }

            const lengthVariation = variationAttributes?.find(
                (variation) => variation.id === 'length'
            )
            const sizeVariation = variationAttributes?.find((variation) => variation.id === 'size')
            const selectedSize = sizeVariation?.selectedValue?.value
            const selectedLength = lengthVariation?.selectedValue?.value
            const hasLengthVariation = lengthVariation?.values?.length > 0

            if (variant && selectedSize && !hasLengthVariation) {
                const foundInArray = product?.c_notifyMeArray?.find(
                    ({size}) => size === selectedSize
                )
                return foundInArray?.size === selectedSize && !variant?.orderable
            }

            if (variant && hasLengthVariation && selectedSize && selectedLength) {
                const notifyMeSelectedVariation = product?.c_notifyMeArray?.find(
                    ({size, length}) => size === selectedSize && length === selectedLength
                )
                return (
                    notifyMeSelectedVariation?.size === selectedSize &&
                    notifyMeSelectedVariation?.length === selectedLength &&
                    !variant?.orderable
                )
            }

            return false
        }, [notifyMeEnabled, variationAttributes, variant, product])
        const priceData = useMemo(() => {
            // When a specific variant is selected, calculate price for that variant
            // This uses variant-level productPromotions if available (requires BD fix to Product API)
            if (variant?.productId && product?.variants) {
                const selectedVariant = product.variants.find(
                    (v) => v.productId === variant.productId
                )
                if (selectedVariant) {
                    // Use variant's own promotions, or fallback to product-level promotions
                    const variantWithPromotions = {
                        ...selectedVariant,
                        productPromotions:
                            selectedVariant.productPromotions || product?.productPromotions
                    }
                    return getPriceData(variantWithPromotions, {quantity})
                }
            }
            // No variant selected - show lowest price across all variants
            return getPriceData(product, {quantity})
        }, [product, quantity, variant?.productId])
        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type.set
        const isProductABundle = product?.type.bundle
        const errorContainerRef = useRef(null)
        const hasScrolledToErrorRef = useRef(false)
        const isDesktop = useBreakpointValue({base: false, lg: true})
        const {headerHeight} = useHeaderContext()
        const {ref: addToCartRef, isInView} = useInView({
            rootMargin: `-${headerHeight}px 0px 100% 0px`
        })
        const {setPdpStickyButtonActive} = useStickyState()
        const {showCustomToast} = useCustomToast()
        const productDetailsRef = useRef()
        const giftCardFormContainerRef = useRef(null)
        const fitValue = product?.c_bvHowDoesItFitAverageRating
        const {reviews: bazarVoiceReviews} = useBazarVoice({pid: product?.id})
        const hasBazaarVoiceReviews = (bazarVoiceReviews?.Results?.length || 0) > 0
        const [isShopSimilarOpen, setIsShopSimilarOpen] = useState(false)

        const MAX_PER_PRODUCT = product?.c_productMaxOrderQty || 100
        const productInBasket = basket?.productItems?.find((item) => item.productId === product?.id)
        const quantityInBasket = productInBasket?.quantity || 0
        const hasReachedMaxQuantity = quantityInBasket >= MAX_PER_PRODUCT
        // Only check ATS limit if we have actual inventory data (not during SSR)
        const hasReachedATS =
            product?.inventory?.ats != null && productInBasket?.quantity === product?.inventory?.ats

        // Gift card hook - extracts all gift card related state and logic
        const {
            isGiftPage,
            giftCardFormRef,
            giftCardOrderableState,
            isCheckBalanceOpen,
            handleCheckBalanceOpen,
            handleCheckBalanceClose,
            validateGiftCardForm,
            getGiftCardData,
            resetGiftCardForm,
            getGiftCardErrorMessage,
            hasGiftCardFormErrors
        } = useGiftCard(product, {
            variant,
            variationAttributes,
            isOutOfStock,
            showInventoryMessage
        })

        const {disableButton, customInventoryMessage} = useMemo(() => {
            // Don't show inventory messages until availability data is loaded
            const hasAvailabilityData = product?.inventory != null

            let currentInventoryMsg = ''

            // Use gift card orderable state from hook for gift cards
            // For non-gift cards, use the standard logic
            let shouldDisableButton = isGiftPage
                ? giftCardOrderableState.isGiftCardDisabled
                : (() => {
                      const hasUnselectedVariations = variationAttributes?.some(
                          (attr) => !attr.selectedValue?.value
                      )
                      const allVariationsSelected =
                          variationAttributes?.length > 0 &&
                          variationAttributes.every((attr) => attr.selectedValue?.value)

                      if (hasUnselectedVariations) return false
                      if (allVariationsSelected && !variant) return true
                      if (variant && !variant.orderable) return true
                      if (variant && variant.orderable) return false
                      return showInventoryMessage || isOutOfStock
                  })()

            if (hasReachedMaxQuantity || hasReachedATS) {
                shouldDisableButton = true
                currentInventoryMsg = intl.formatMessage({
                    defaultMessage: 'Max quantity in cart',
                    id: 'product_view.error.max_quantity_reached'
                })
            }

            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            // Only show inventory messages if we have availability data
            return {
                disableButton: shouldDisableButton,
                customInventoryMessage: hasAvailabilityData ? currentInventoryMsg : ''
            }
        }, [
            showInventoryMessage,
            childProductOrderability,
            hasReachedMaxQuantity,
            hasReachedATS,
            variant,
            variationAttributes,
            product,
            isOutOfStock,
            isGiftPage,
            giftCardOrderableState
        ])

        const validateAndShowError = async (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            let showError = !isProductASet && !isProductABundle && !hasValidSelection

            // If this is an eGift card, also validate the gift card form (using hook)
            const giftCardFormValid = await validateGiftCardForm()
            if (!giftCardFormValid) {
                showError = true
            }

            const scrollToError =
                showError && scrollErrorIntoView && !hasScrolledToErrorRef.current

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
                hasScrolledToErrorRef.current = true
            }

            return hasValidSelection && giftCardFormValid
        }

        const handleNotifyMeCloseModal = () => {
            setIsNotifyMeModalOpen(false)
            setNotifyMeSuccessContent(false)
        }

        const handleShopSimilarOpenModal = () => {
            sendClickEvent('recommendation_clicked')
            setIsShopSimilarOpen(true)
        }

        const handleShopSimilarCloseModal = () => {
            setIsShopSimilarOpen(false)
        }

        const renderActionButtons = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            const buttons = []

            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Bag',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Bag',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Bag',
                    id: 'product_view.button.add_bundle_to_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                })
            }

            const showError = () => {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }

            const handleCartItem = async () => {
                const hasValidSelection = await validateAndShowError({scrollErrorIntoView})

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null

                // Get gift card data from hook (returns null if not a gift card)
                const giftCardData = getGiftCardData()

                if (updateCart) {
                    await updateCart(variant || product, quantity, giftCardData)
                    // Reset the gift card form after successful update
                    resetGiftCardForm()
                    return
                }

                try {
                    const itemsAdded = await addToCart(variant, quantity, giftCardData)
                    if (itemsAdded) {
                        // Reset the gift card form after successful add
                        resetGiftCardForm()

                        if (isDesktop) {
                            onAddToCartModalOpen()
                            setTimeout(() => {
                                onAddToCartModalClose()
                            }, 2000)
                        } else {
                            setTimeout(() => {
                                showCustomToast({
                                    message: 'Added to Bag',
                                    action: (
                                        <Button
                                            variant="link"
                                            fontWeight="medium"
                                            fontSize="sm"
                                            onClick={() => navigate('/cart')}
                                            colorScheme="white"
                                            textDecoration="underline"
                                        >
                                            {intl.formatMessage({
                                                id: 'add_to_cart_modal.link.view_bag',
                                                defaultMessage: 'View Bag'
                                            })}
                                        </Button>
                                    ),
                                    styles: {
                                        position: 'relative',
                                        top: 'auto',
                                        width: '100%',
                                        right: 0,
                                        left: 0
                                    },
                                    containerId: 'app-header'
                                })
                            }, 500)
                        }
                    }
                } catch (e) {
                    showError()
                }
            }

            const handleWishlistItem = async () => {
                if (!updateWishlist && !addToWishlist) return null
                if (updateWishlist) {
                    updateWishlist(product, variant, quantity)
                    return
                }
                addToWishlist(product, variant, quantity)
            }

            const handleNotifyMe = async () => {
                if (customer?.isRegistered) {
                    const formData = {
                        notifyMeEmail: customer?.email,
                        notifyMeTermsAndConditions: false,
                        notifyMeSKU: product?.id
                    }
                    const notifyMeResponse = await submitNotifyMe({
                        formData: JSON.stringify(formData)
                    })

                    if (
                        notifyMeResponse &&
                        notifyMeResponse?.requestId &&
                        notifyMeResponse?.resultMessages.length === 0
                    ) {
                        setNotifyMeSuccessContent(true)
                        setIsNotifyMeModalOpen(true)
                    } else {
                        setNotifyMeSuccessContent(false)
                        setIsNotifyMeModalOpen(false)
                        setNotifyMeError(intl.formatMessage(messages.notifyMeError))
                    }
                } else {
                    setIsNotifyMeModalOpen(true)
                }
            }

            // Mutually exclusive button logic
            const isCtaResolving =
                isProductLoading ||
                isMasterProductChanging ||
                showLoading ||
                (variant && !Object.prototype.hasOwnProperty.call(variant, 'orderable'))
            const shouldShowNotifyMe = isNotifyMe
            const shouldShowShopSimilar =
                config.app.VICKY_ENABLED === 'true' &&
                !product?.c_isRestock &&
                variant &&
                typeof variant.orderable === 'boolean' &&
                variant.orderable === false &&
                !isNotifyMe
            const shouldShowAddToCart =
                !isNotifyMe &&
                !shouldShowShopSimilar &&
                (!variant ||
                    (typeof variant?.orderable === 'boolean' && variant.orderable === true))
            const ctaLoading =  isCtaResolving || isBasketLoading

            if ((addToCart || updateCart) && !isProductPartOfBundle) {
                buttons.push(
                    <ButtonGroup
                        key="cart-wishlist-group"
                        size="md"
                        isAttached
                        variant="solid"
                        width="100%"
                    >
                        {(() => {
                            const isVickyEnabled = config.app.VICKY_ENABLED === 'true'
                            const isLoadingCta =
                                !shouldShowNotifyMe &&
                                !shouldShowShopSimilar &&
                                !shouldShowAddToCart &&
                                isVickyEnabled

                            if (isLoadingCta) {
                                return (
                                    <Button
                                        fontSize="md"
                                        fontWeight={700}
                                        textTransform="uppercase"
                                        isLoading={true}
                                        isDisabled={true}
                                        flex="1"
                                        h="48px"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Loading"
                                            id="pdp_cta.loading"
                                        />
                                    </Button>
                                )
                            }

                            if (shouldShowShopSimilar) {
                                return (
                                    <Button
                                        fontSize="md"
                                        fontWeight={700}
                                        onClick={handleShopSimilarOpenModal}
                                        textTransform="uppercase"
                                        isLoading={ctaLoading}
                                        flex="1"
                                        h="48px"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Shop similar"
                                            id="pdp_shop_similar.cta.label"
                                        />
                                    </Button>
                                )
                            }

                            if (shouldShowNotifyMe) {
                                return (
                                    <Button
                                        key="notify-me-button"
                                        onClick={handleNotifyMe}
                                        isLoading={ctaLoading || isNotifyMeLoading}
                                        width="100%"
                                        variant="solid"
                                        marginBottom={4}
                                        flex="1"
                                        h="48px"
                                        fontSize={{base: 'md'}}
                                        lineHeight={{base: '20px'}}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Notify Me"
                                            id="pdp_notify_me_cta.label"
                                        />
                                    </Button>
                                )
                            }

                            return (
                                <Button
                                    fontSize="md"
                                    fontWeight={700}
                                    textTransform="uppercase"
                                    onClick={handleCartItem}
                                    isDisabled={disableButton}
                                    width={'100%'}
                                    isLoading={ctaLoading}
                                    aria-label={
                                        updateCart
                                            ? buttonText.update
                                            : isProductASet
                                              ? buttonText.addSetToCart
                                              : isProductABundle
                                                ? buttonText.addBundleToCart
                                                : buttonText.addToCart
                                    }
                                    flex="1"
                                    h="48px"
                                    data-testid={`add-to-bag${disableButton ? '-disabled' : ''}`}
                                >
                                    {updateCart
                                        ? buttonText.update
                                        : isProductASet
                                          ? buttonText.addSetToCart
                                          : isProductABundle
                                            ? buttonText.addBundleToCart
                                            : buttonText.addToCart}
                                </Button>
                            )
                        })()}

                        {/* Wishlist CTA - always available when enabled (hidden for gift cards) */}
                        {(addToWishlist || updateWishlist) && canAddToWishlist && !isGiftPage && (
                            <IconButtonWithRegistrationForWishlist
                                variant="outline"
                                onClick={handleWishlistItem}
                                isDisabled={isWishlistLoading || !canAddToWishlist}
                                isLoading={isWishlistLoading}
                                aria-label={
                                    isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'
                                }
                                icon={isInWishlist ? <HeartSolidIcon /> : <HeartIcon />}
                                border="1px solid"
                                borderColor="black"
                                bg="white"
                                h="48px"
                                minWidth="48px"
                            />
                        )}
                    </ButtonGroup>
                )
            } else if ((addToWishlist || updateWishlist) && !isProductPartOfBundle && !isGiftPage) {
                buttons.push(
                    <ButtonWithRegistrationForWishlist
                        key="wishlist-only-button"
                        onClick={handleWishlistItem}
                        disabled={isWishlistLoading || !canAddToWishlist}
                        isLoading={isWishlistLoading}
                        width="100%"
                        variant="outline"
                        marginBottom={4}
                        leftIcon={isInWishlist ? <HeartSolidIcon /> : <HeartIcon />}
                    >
                        {updateWishlist ? buttonText.update : buttonText.addToWishlist}
                    </ButtonWithRegistrationForWishlist>
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        const openFindInStoreModal = () => {
            onFindInStoreOpen()
        }

        useEffect(() => {
            setPdpStickyButtonActive(!isInView)
        }, [isInView])

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (!showOptionsMessage) {
                hasScrolledToErrorRef.current = false
            }
        }, [showOptionsMessage])


        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            const selectedSize = variationAttributes?.find((attr) => attr.id === 'size')
                ?.selectedValue?.value

            if (selectedSize) {
                const sizeFewItemsLeft = product?.c_fewItemsLeftArray?.find(
                    ({size}) => size === selectedSize
                )
                setIsFewItemsLeft(!!sizeFewItemsLeft)
            } else {
                setIsFewItemsLeft(!!product?.c_fewItemsLeft && !product?.c_isEGiftCard)
            }
        }, [product, variationAttributes])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const {missingAttributeMessages, giftCardError} = useMemo(() => {
            const messages = {}
            const giftCardMessage = getGiftCardErrorMessage(variationAttributes);

            (variationAttributes || []).forEach((attr) => {
                if (!attr.selectedValue?.value) {
                    messages[attr.id] = intl.formatMessage(
                        {
                            defaultMessage: 'Please select a {attribute}',
                            id: 'product_view.error.missing_single_attribute'
                        },
                        {attribute: attr.name.toLowerCase()}
                    )
                }
            })

            return {
                missingAttributeMessages: messages,
                giftCardError: giftCardMessage
            }
        }, [variationAttributes, intl, getGiftCardErrorMessage])

        const notifyMeIcon = (value, orderable) => {
            let hasSizeVariation = product?.variationAttributes?.find((attr) => attr.id === 'size')
            let hasLengthVariation = product?.variationAttributes?.find(
                (attr) => attr.id === 'length'
            )
            let showNotifyMeIcon = false

            if (hasSizeVariation && !hasLengthVariation) {
                let notifyMeVariations = product?.c_notifyMeArray?.find(({size}) => size === value)
                showNotifyMeIcon =
                    notifyMeEnabled && notifyMeVariations?.size === value && !orderable
            }

            if (hasSizeVariation && hasLengthVariation) {
                // Use variationParams.length (URL-selected) instead of product.variationValues.length (default)
                const selectedLength = variationParams?.length
                if (selectedLength) {
                    let notifyMeVariations = product?.c_notifyMeArray?.find(
                        ({size, length}) => size === value && length === selectedLength
                    )
                    showNotifyMeIcon =
                        notifyMeEnabled &&
                        notifyMeVariations?.size === value &&
                        notifyMeVariations?.length === selectedLength &&
                        !orderable
                }
            }

            return showNotifyMeIcon
        }

        return (
            <Flex direction={'column'} data-testid="product-view" ref={ref}>
                {!isGiftPage && <KlarnaScript />}

                {/* Basic information etc. title, price, breadcrumb*/}
                {isDesktop && (
                    <Skeleton isLoaded={category} minWidth={64}>
                        <Breadcrumb categories={category} mb={2} />
                    </Skeleton>
                )}
                <Flex direction={['column', 'column', 'column', 'row']}>
                    {showImageGallery && (
                        <Box
                            flex={{base: 1, lg: '2 0'}}
                            mr={[0, 0, 0, '90px', '90px']}
                            {...(isGiftPage && {
                                position: {base: 'relative', lg: 'sticky'},
                                top: {base: 'auto', lg: '0'},
                                height: {base: 'auto', lg: 'calc(100vh - 80px)'},
                                alignSelf: 'flex-start'
                            })}
                        >
                            {product && (!isProductLoading || isGiftPage) ? (
                                <>
                                    <ImageGallery
                                        size={imageSize}
                                        imageGroups={product.imageGroups}
                                        selectedVariationAttributes={variationParams}
                                        badges={product?.c_badges}
                                        isGiftPage={isGiftPage}
                                    />
                                    <HideOnMobile>
                                        {showFullLink && product && (
                                            <Link
                                                to={`/product/${product.master.masterId}`}
                                                color="blue.600"
                                            >
                                                <FormattedMessage
                                                    id="product_view.link.full_details"
                                                    defaultMessage="See full details"
                                                />
                                            </Link>
                                        )}
                                    </HideOnMobile>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        align="stretch"
                        spacing={{base: 6, lg: 8}}
                        flex={1}
                        height="100%"
                        {...(!isGiftPage && {
                            position: 'sticky',
                            top: '72px'
                        })}
                        mr={[0, 0, 0, '90px', '90px']}
                        ref={productDetailsRef}
                    >
                        <Box>
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isProductPartOfBundle={isProductPartOfBundle}
                                onReviewsClick={onReviewsClick}
                                isGiftPage={isGiftPage}
                                isLoading={isMasterProductChanging}
                            />
                        </Box>
                        <VStack align="stretch" spacing={isGiftPage ? {base: 6, lg: 8} : 0}>
                            {isProductPartOfBundle && (
                                <Box>
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}
                            {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                            {showLoading ? (
                                <>
                                    {/* First Attribute Skeleton */}
                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />

                                    {/* Second Attribute Skeleton */}
                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />
                                </>
                            ) : (
                                (variationAttributes || []).map(
                                    ({id, name, selectedValue, values}, subheadingIndex) => {
                                        // Determine if this is an E-Gift Card based on selected cardType
                                        // This ensures instant visual transition when switching card types
                                        const selectedCardType = variationAttributes?.find(
                                            (attr) => attr.id === 'cardType'
                                        )?.selectedValue?.value
                                        const isEGiftCardSelected =
                                            selectedCardType === 'EGC' ||
                                            (!selectedCardType && product.c_isEGiftCard)

                                        const swatches = values.map(
                                            ({href, name, image, value, orderable}, index) => {
                                                const availabilityTestId = `${id}-${orderable ? 'available' : 'unavailable'}`
                                                const content = image ? (
                                                    <Box
                                                        borderRadius={isEGiftCardSelected ? 3 : 0}
                                                        height="100%"
                                                        width="100%"
                                                        backgroundRepeat="no-repeat"
                                                        backgroundSize="cover"
                                                        backgroundColor={name.toLowerCase()}
                                                        backgroundPosition="center center"
                                                        backgroundImage={`url(${
                                                            image.disBaseLink || image.link
                                                        })`}
                                                        data-testid={availabilityTestId}
                                                    />
                                                ) : (
                                                    <Box
                                                        as="span"
                                                        className="swatch-content"
                                                        data-testid={availabilityTestId}
                                                    >
                                                        {name}
                                                    </Box>
                                                )
                                                const hasSelection = Boolean(selectedValue?.value)
                                                const isSelected = selectedValue?.value === value
                                                const isFirst = index === 0
                                                // To mimic the behavior of a native radio input, only
                                                // one swatch should receive tab focus; the rest can be
                                                // selected using arrow keys when the swatch group has
                                                // focus. The focused element is the selected option or
                                                // the first in the group, if no option is selected.
                                                // This is a slight difference, for simplicity, from the
                                                // native element, where the first element is focused on
                                                // `Tab` and the _last_ element is focused on `Shift+Tab`
                                                const isFocusable =
                                                    isSelected || (!hasSelection && isFirst)

                                                const showNotifyMeIcon = notifyMeIcon(
                                                    value,
                                                    orderable
                                                )
                                                const fewItemsLeft =
                                                    product?.c_fewItemsLeftArray?.find(
                                                        ({size}) => size === value
                                                    )

                                                return (
                                                    <Swatch
                                                        isPDP
                                                        key={value}
                                                        href={href}
                                                        disabled={!orderable}
                                                        value={value}
                                                        name={name}
                                                        variant={
                                                            id === 'color'
                                                                ? isEGiftCardSelected
                                                                    ? 'giftCard'
                                                                    : 'circle'
                                                                : 'square'
                                                        }
                                                        selected={isSelected}
                                                        isFocusable={isFocusable}
                                                        id={id}
                                                    >
                                                        {showNotifyMeIcon && (
                                                            <NotifyMeEnvelope
                                                                width="16px"
                                                                height="16px"
                                                                position="absolute"
                                                                top="-5px"
                                                                right="2px"
                                                            />
                                                        )}
                                                        {!showNotifyMeIcon &&
                                                            orderable &&
                                                            fewItemsLeft && (
                                                                <FewItemsLeftIcon
                                                                    position="absolute"
                                                                    top="1"
                                                                    right="1"
                                                                />
                                                            )}
                                                        {content}
                                                    </Swatch>
                                                )
                                            }
                                        )
                                        const isLastAttribute =
                                            subheadingIndex === variationAttributes.length - 1
                                        return (
                                            <React.Fragment key={id}>
                                                {id === 'size' && (
                                                    <Flex mt={{base: 6}}>
                                                        {fitValue && hasBazaarVoiceReviews ? (
                                                            <ActualFitMessage
                                                                fit={fitValue}
                                                                onClick={onReviewsClick}
                                                            />
                                                        ) : (
                                                            <Text
                                                                fontSize="xs"
                                                                fontStyle="normal"
                                                                fontWeight={400}
                                                                lineHeight="15px"
                                                            >
                                                                {intl.formatMessage({
                                                                    defaultMessage: 'Size',
                                                                    id: 'product_list.button.size'
                                                                })}
                                                            </Text>
                                                        )}
                                                        <SizeChartDrawer
                                                            optimizelyContent={optimizelyContent}
                                                            category={category}
                                                        />
                                                    </Flex>
                                                )}
                                                <Box mt={id === 'length' && 6}>
                                                    <SwatchGroup
                                                        isPDP
                                                        isSizeSwatch={id !== 'color'}
                                                        giftCardType={id === 'cardType'}
                                                        giftCardValue={id === 'priceValue'}
                                                        giftECard={
                                                            id === 'color' && product.c_isEGiftCard
                                                        }
                                                        value={selectedValue?.value}
                                                        displayValueOnly={
                                                            isGiftPage
                                                                ? false
                                                                : ['color', 'size'].includes(id)
                                                        }
                                                        displayLabelOnly={isGiftPage ? true : false}
                                                        displayName={
                                                            selectedValue?.name ||
                                                            selectedValue?.name ||
                                                            ''
                                                        }
                                                        label={intl.formatMessage(
                                                            {
                                                                defaultMessage: '{variantType}',
                                                                id: 'product_view.label.variant_type'
                                                            },
                                                            {
                                                                variantType: isGiftPage
                                                                    ? `${
                                                                          subheadingIndex + 1
                                                                      }. ${name}`
                                                                    : name
                                                            }
                                                        )}
                                                        id={id}
                                                    >
                                                        {swatches}
                                                    </SwatchGroup>
                                                    {!showLoading &&
                                                        showOptionsMessage &&
                                                        missingAttributeMessages[id] && (
                                                            <Fade in={true}>
                                                                <Text
                                                                    fontSize="xs"
                                                                    fontWeight={400}
                                                                    color="dangerText"
                                                                    mt={2}
                                                                >
                                                                    {missingAttributeMessages[id]}
                                                                </Text>
                                                            </Fade>
                                                        )}
                                                    {isLastAttribute &&
                                                        !showLoading &&
                                                        customInventoryMessage && (
                                                            <Fade in={true}>
                                                                <Text
                                                                    fontSize="xs"
                                                                    color="dangerText"
                                                                    mt={2}
                                                                >
                                                                    {customInventoryMessage}
                                                                </Text>
                                                            </Fade>
                                                        )}
                                                </Box>
                                            </React.Fragment>
                                        )
                                    }
                                )
                            )}

                            {/* Gift Card Form - Show for eGift Cards */}
                            <Box ref={giftCardFormContainerRef}>
                                <GiftCardForm
                                    ref={giftCardFormRef}
                                    isVisible={product?.c_isEGiftCard}
                                    headingIndex={(variationAttributes?.length || 0) + 1}
                                />
                                <GiftCardCheckBalance
                                    isOpen={isCheckBalanceOpen}
                                    onClose={handleCheckBalanceClose}
                                />

                                {!isNotifyMe && isFewItemsLeft && variant?.orderable && <FewItemsLeftMessage />}
                                <Box ref={errorContainerRef}>
                                    {!showLoading && showOptionsMessage && giftCardError && (
                                        <Fade in={true}>
                                            <Text
                                                fontSize="xs"
                                                fontWeight={400}
                                                color="dangerText"
                                                marginBottom={4}
                                                marginTop={isGiftPage ? 4 : 0}
                                            >
                                                {giftCardError}
                                            </Text>
                                        </Fade>
                                    )}
                                </Box>
                                <HideOnDesktop>
                                    {showFullLink && product && (
                                        <Link
                                            to={`/product/${product.master.masterId}`}
                                            color="blue.600"
                                        >
                                            <FormattedMessage
                                                id="product_view.link.full_details"
                                                defaultMessage="See full details"
                                            />
                                        </Link>
                                    )}
                                </HideOnDesktop>
                                {isProductASet && <p>{product?.shortDescription}</p>}
                            </Box>
                        </VStack>

                        <Box>
                            <Box ref={addToCartRef} display={'block'}>
                                {renderActionButtons({scrollErrorIntoView: true})}
                            </Box>

                            {!isGiftPage && (
                                <Box>
                                    <klarna-placement
                                        data-key="credit-promotion-auto-size"
                                        data-locale="en-GB"
                                        data-purchase-amount={
                                            priceData?.currentPrice *
                                                (priceData?.quantity ?? 1) *
                                                100 || 0
                                        }
                                    />
                                </Box>
                            )}
                        </Box>

                        {isGiftPage && <GiftCallout />}

                        {/* Loyalty program */}
                        {!isGiftPage && (
                            <Box>
                                <Loyalty points={product?.c_loyaltyPointsToEarn} />
                            </Box>
                        )}
                        <InformationAccordion
                            product={product}
                            openFindInStoreModal={openFindInStoreModal}
                            optimizelyContent={optimizelyContent}
                            isOptimizelyLoading={isOptimizelyLoading}
                            isGiftPage={isGiftPage}
                            checkBalanceOnOpen={handleCheckBalanceOpen}
                        />
                        {(() => {
                            // Search for ProductRecommendationCarouselBlock in Optimizely content
                            const findProductRecommendationBlock = (content) => {
                                if (!content || typeof content !== 'object') return null

                                if (content.__typename === 'ProductRecommendationCarouselBlock') {
                                    return content
                                }

                                // Search recursively through object properties
                                for (const [key, value] of Object.entries(content)) {
                                    if (Array.isArray(value)) {
                                        for (const item of value) {
                                            const found = findProductRecommendationBlock(item)
                                            if (found) return found
                                        }
                                    } else if (value && typeof value === 'object') {
                                        const found = findProductRecommendationBlock(value)
                                        if (found) return found
                                    }
                                }
                                return null
                            }

                            const shopTheLookConfig =
                                findProductRecommendationBlock(optimizelyContent)

                            return <ShopTheLook product={product} {...(shopTheLookConfig || {})} />
                        })()}
                    </VStack>
                </Flex>

                {!isInView && (
                        <StickyButton
                            name={product?.name}
                            priceData={priceData}
                            currency={product?.currency || activeCurrency}
                            isProductPartOfBundle={isProductPartOfBundle}
                            renderActionButtons={renderActionButtons}
                            productDetailsRef={productDetailsRef}
                            giftCardFormContainerRef={giftCardFormContainerRef}
                            hasGiftCardFormErrors={hasGiftCardFormErrors}
                            variationAttributes={variationAttributes}
                            showLoading={showLoading}
                            intl={intl}
                        actualFitComponent={
                            fitValue && hasBazaarVoiceReviews ? (
                                <ActualFitMessage fit={fitValue} onClick={onReviewsClick} />
                            ) : null
                        }
                        sizeChartComponent={
                            <SizeChartDrawer
                                optimizelyContent={optimizelyContent}
                                category={category}
                            />
                        }
                    />
                )}

                {isNotifyMe && (
                    <NotifyMeModal
                        key="notify-me-modal"
                        isOpen={isNotifyMeModalOpen}
                        onClose={handleNotifyMeCloseModal}
                        variant={variant}
                        product={product}
                        showSuccessContent={notifyMeSuccessContent}
                    />
                )}

                <ShopSimilarModal
                    key="shop-similar-modal"
                    isOpen={isShopSimilarOpen}
                    onClose={handleShopSimilarCloseModal}
                    product={variant || product}
                />
            </Flex>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isMasterProductChanging: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    onFindInStoreOpen: PropTypes.func,
    optimizelyContent: PropTypes.object,
    isOptimizelyLoading: PropTypes.bool,
    isInWishlist: PropTypes.bool,
    onReviewsClick: PropTypes.func,
    basket: PropTypes.object
}

export default ProductView
